<template>
    <el-dropdown v-if="!readOnly" class="cursor" :disabled="globalLoading || disableDropdown" @visible-change="handleDropdownVisibleChange">
        <ComStatusTag :status="modelValue" :loading="globalLoading" />
        <template #dropdown>
            <el-dropdown-menu>
                <el-dropdown-item
                    v-if="modelValue == HuonekorttiStatusResponse.Tyo"
                    @click="julkaiseHuonekortti(huonekortti.id)">
                    Julkaise
                </el-dropdown-item>
                <template v-if="modelValue == HuonekorttiStatusResponse.Julkaistu">
                    <el-dropdown-item v-if="paakayttaja" @click="hyvaksyHuonekortti(huonekortti.id)">
                        Hyväksy
                    </el-dropdown-item>
                    <el-dropdown-item v-else @click="hyvaksytaHuonekortti(huonekortti.id)">
                        Lähetä hyväksyttäväksi
                    </el-dropdown-item>
                </template>
                <template v-if="modelValue == HuonekorttiStatusResponse.Odottaa">
                    <template v-if="paakayttaja">
                        <el-dropdown-item @click="hyvaksyHuonekortti(huonekortti.id)">Hyväksy</el-dropdown-item>
                        <el-dropdown-item @click="hylkaaHuonekortti(huonekortti.id)">Hylkää</el-dropdown-item>
                    </template>
                    <template v-else>
                        <el-dropdown-item @click="hylkaaHuonekortti(huonekortti.id)">Peruuta</el-dropdown-item>
                    </template>
                </template>
                <template v-if="modelValue == HuonekorttiStatusResponse.Hyvaksytty && paakayttaja">
                    <el-dropdown-item v-if="historiaLoading" disabled>
                        <el-icon class="is-loading"><Loading /></el-icon>
                    </el-dropdown-item>
                    <el-dropdown-item v-else-if="voiPalauttaa" @click="palautaHyvaksyttyHuonekortti(huonekortti.id)">
                        Palauta
                    </el-dropdown-item>
                </template>
            </el-dropdown-menu>
        </template>
    </el-dropdown>
    <ComStatusTag v-else :status="modelValue" />
</template>

<script setup lang="ts">
import { HuonekorttiResponse } from "@/api/models/huonekortti-response"
import { HuonekorttiStatusResponse } from "@/api/models/huonekortti-status-response"
import ComStatusTag from "@/components/common/ComStatusTag.vue"
import { computed, ref } from "vue"
import { huonekorttiListing } from "./huonekortit"
import { Loading } from "@element-plus/icons-vue"
import {
    useHylkaaHuonekortti,
    useHyvaksyHuonekortti,
    useHyvaksytaHuonekortti,
    useJulkaiseHuonekortti,
    usePalautaHyvaksyttyHuonekortti,
} from "@/methods/huonekorttiComposablses"
import { useHuonekorttiHistoria } from "@/methods/huonekorttiHistoriaComposables"

const props = defineProps<{
    modelValue: HuonekorttiStatusResponse
    huonekortti: HuonekorttiResponse
}>()

const { writableVastuuyksikot, paakayttaja, updateHuonekortit } = huonekorttiListing()

const { julkaiseHuonekortti, loading: julkaiseLoading } = useJulkaiseHuonekortti({
    onSuccess: () => updateHuonekortit(),
    onError: () => {},
    errorMessage: "Julkaisu epäonnistui"
})

const { hylkaaHuonekortti, loading: hylkaaLoading } = useHylkaaHuonekortti({
    onSuccess: () => updateHuonekortit(),
    onError: () => {},
    errorMessage: "Hylkäys epäonnistui"
})

const { hyvaksyHuonekortti, loading: hyvaksyLoading } = useHyvaksyHuonekortti({
    onSuccess: () => updateHuonekortit(),
    onError: () => {},
    errorMessage: "Hyväksyminen epäonnistui"
})

const { hyvaksytaHuonekortti, loading: hyvaksytaLoading } = useHyvaksytaHuonekortti({
    onSuccess: () => updateHuonekortit(),
    onError: () => {},
    errorMessage: "Hyväksyttäväksi lähettäminen epäonnistui"
})

const { palautaHyvaksyttyHuonekortti, loading: palautaHyvaksyttyLoading } = usePalautaHyvaksyttyHuonekortti({
    onSuccess: () => updateHuonekortit(),
    onError: () => {},
    errorMessage: "Palauttaminen epäonnistui"
})

const globalLoading = computed(() =>
    julkaiseLoading.value ||
    hylkaaLoading.value ||
    hyvaksyLoading.value ||
    hyvaksytaLoading.value ||
    palautaHyvaksyttyLoading.value
)

const readOnly = computed(() =>
    !writableVastuuyksikot.value.some(o => o.id === props.huonekortti.vastuuyksikko.id)
)

const shouldFetchHistoria = ref(false)

const huonekorttiId = computed(() =>
    shouldFetchHistoria.value &&
    props.modelValue === HuonekorttiStatusResponse.Hyvaksytty &&
    paakayttaja.value
        ? props.huonekortti.id
        : undefined
)

const { historia, loading: historiaLoading } = useHuonekorttiHistoria(huonekorttiId)

const handleDropdownVisibleChange = (visible: boolean) => {
    if (visible && !shouldFetchHistoria.value &&
        props.modelValue === HuonekorttiStatusResponse.Hyvaksytty &&
        paakayttaja.value) {
        shouldFetchHistoria.value = true
    }
}

const voiPalauttaa = computed(() => {
    if (!historia.value) return false
    const onMuutoksia =
        (historia.value.huonekortti?.length ?? 0) > 0 ||
        (historia.value.nimikkeet?.length ?? 0) > 0 ||
        (historia.value.tilat?.length ?? 0) > 0
    return !onMuutoksia
})

const disableDropdown = computed(() => {
    if (props.modelValue === HuonekorttiStatusResponse.Hyvaksytty 
        && shouldFetchHistoria.value
        && !historiaLoading.value 
        && !voiPalauttaa.value
    ) {
        return true
    }
    return false
})
</script>
<style scoped>
.cursor {
    cursor: pointer;
}
</style>
